import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../pages/AddDataPage/styles.css';
import { API_BASE_URL as API_URL } from '../config/apiBase';


const AddDataContent = ({ schemaId, isInline = false, onBack, adminMode = false, entityId = null, onSuccess }) => {
  const navigate = useNavigate();
  const [schema, setSchema] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [file, setFile] = useState(null);
  const [inputMode, setInputMode] = useState('upload');

  useEffect(() => {
    if (schemaId) {
      fetchSchema();
    }
  }, [schemaId]);

  const fetchSchema = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use admin route if in admin mode, otherwise use regular route
      const url = adminMode && entityId 
        ? `${API_URL}/admin/schemas/${schemaId}?entity_id=${entityId}`
        : `${API_URL}/schemas/${schemaId}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch schema');
      }

      const data = await response.json();
      setSchema(data.data);
      
      // Initialize form data with empty values
      const initialFormData = {};
      const columns = data.data.schema_definition.columns || [];
      columns.forEach(column => {
        initialFormData[column.name] = '';
      });
      setFormData(initialFormData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = ['.csv', '.xlsx', '.xls'];
      const fileExt = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
      
      if (!allowedTypes.includes(fileExt)) {
        setError('Please select a CSV or Excel file');
        return;
      }

      if (selectedFile.size > 10 * 1024 * 1024) { // 10MB limit
        setError('File size must be less than 10MB');
        return;
      }

      setFile(selectedFile);
      setError('');
    }
  };

  const handleFileUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', file);
      formDataToSend.append('schema_id', schemaId);
      if (adminMode && entityId) {
        formDataToSend.append('entity_id', entityId);
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/visuals/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Upload failed');
      }

      setSuccess(`Successfully uploaded ${data.insertedCount} records`);
      setFile(null);
      const fileInput = document.getElementById(`file-input-${schemaId}`);
      if (fileInput) {
        fileInput.value = '';
      }
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 1500);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate required fields
    const columns = schema.schema_definition.columns || [];
    const requiredColumns = columns.filter(col => col.required);
    
    for (const column of requiredColumns) {
      if (!formData[column.name] || formData[column.name] === '') {
        setError(`Required field '${column.name}' is missing`);
        return;
      }
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const requestBody = {
        schema_id: schemaId,
        data: formData
      };

      if (adminMode && entityId) {
        requestBody.entity_id = entityId;
      }

      const response = await fetch(`${API_URL}/admin/data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to insert data');
      }

      setSuccess('Data inserted successfully!');
      
      // Reset form
      const initialFormData = {};
      columns.forEach(column => {
        initialFormData[column.name] = '';
      });
      setFormData(initialFormData);
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 1500);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderInput = (column) => {
    const value = formData[column.name] || '';
    
    switch (column.type) {
      case 'integer':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleInputChange(column.name, e.target.value)}
            className="form-input"
            placeholder={`Enter ${column.name}`}
          />
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleInputChange(column.name, e.target.value)}
            className="form-input"
          />
        );
      
      case 'boolean':
        return (
          <select
            value={value}
            onChange={(e) => handleInputChange(column.name, e.target.value)}
            className="form-select"
          >
            <option value="">Select...</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );
      
      case 'email':
        return (
          <input
            type="email"
            value={value}
            onChange={(e) => handleInputChange(column.name, e.target.value)}
            className="form-input"
            placeholder={`Enter ${column.name}`}
          />
        );
      
      default: // text
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleInputChange(column.name, e.target.value)}
            className="form-input"
            placeholder={`Enter ${column.name}`}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className={isInline ? "add-data-inline-loading" : "add-data-loading"}>
        <div className="loading-spinner"></div>
        <p>Loading schema...</p>
      </div>
    );
  }

  if (!schema) {
    return (
      <div className={isInline ? "add-data-inline-error" : "add-data-error"}>
        <h3>Schema not found</h3>
        <p>The requested table schema could not be found.</p>
      </div>
    );
  }

  const columns = schema.schema_definition.columns || [];
  const containerClass = isInline ? "add-data-inline-container" : "add-data-container";
  const headerClass = isInline ? "add-data-inline-header" : "add-data-header";

  return (
    <div className={containerClass}>
      <div className={headerClass}>
        <div>
          <h1>Add Data to {schema.table_name}</h1>
          <p>Fill in the form below to add a new record</p>
        </div>
        {!isInline && (
          <button 
            onClick={() => {
              if (onBack) {
                onBack();
              } else if (adminMode && entityId) {
                navigate(`/admin/entities/${entityId}/tables`);
              } else {
                navigate('/visuals/tables');
              }
            }}
            className="add-data-back-btn"
          >
            Back to Tables
          </button>
        )}
      </div>

      <div className="add-data-content">
        {/* Input Mode Toggle */}
        <div className="input-mode-toggle">
          <button
            type="button"
            className={`mode-btn ${inputMode === 'upload' ? 'active' : ''}`}
            onClick={() => setInputMode('upload')}
          >
            📁 Upload File
          </button>
          <button
            type="button"
            className={`mode-btn ${inputMode === 'manual' ? 'active' : ''}`}
            onClick={() => setInputMode('manual')}
          >
            📝 Manual Entry
          </button>
        </div>

        {inputMode === 'manual' ? (
          <form onSubmit={handleSubmit} className="add-data-form">
            <div className="form-section">
              <h2>Record Information</h2>
              <div className="form-grid">
                {columns.map((column, index) => (
                  <div key={index} className="form-group">
                    <label htmlFor={column.name}>
                      {column.name}
                      {column.required && <span className="required-asterisk"> *</span>}
                    </label>
                    {renderInput(column)}
                    <div className="field-info">
                      <span className="field-type">{column.type}</span>
                      {column.required && <span className="required-badge">Required</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </form>
        ) : (
          <div className="upload-section">
            <h2>Upload Data File</h2>
            <div className="upload-form">
              <div className="form-group">
                <label>Select File:</label>
                <input
                  id={`file-input-${schemaId}`}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="file-input"
                />
                <div className="file-info">
                  <p>Supported formats: CSV, Excel (.xlsx, .xls)</p>
                  <p>Maximum file size: 10MB</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="form-actions">
          {isInline && (
            <button
              type="button"
              onClick={() => onBack && onBack()}
              className="cancel-btn"
            >
              Cancel
            </button>
          )}
          {inputMode === 'manual' ? (
            <button
              type="submit"
              disabled={submitting}
              className="submit-btn"
              onClick={handleSubmit}
            >
              {submitting ? 'Adding Data...' : 'Add Data'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleFileUpload}
              disabled={!file || uploading}
              className="submit-btn"
            >
              {uploading ? 'Uploading...' : 'Upload File'}
            </button>
          )}
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            {success}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddDataContent;

